import { Button } from '@/components/ui/Button'
import { Fieldset } from '@/components/ui/Fieldset'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { toastService } from '@/lib/toastService'
import { Box, Heading, HStack, SimpleGrid, Stack } from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
    getAllGlobalSettings,
    updateFranchise,
    updateFranchiseCity,
    updateNationalCurrency,
    updateSubscribe,
} from '../api/settingsApi'

/**
 * Global Settings Page
 * Allows superadmin to edit global system settings
 */
export function GlobalSettings() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      currency: '',
      sity: '',
      franshise: '',
      name: '',
      phone: '',
    },
  })

  // Fetch all settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['global-settings'],
    queryFn: getAllGlobalSettings,
  })

  // Populate form when data is loaded
  useEffect(() => {
    if (settings) {
      reset({
        currency: settings.currency?.currency || '',
        sity: settings.city?.sity || '',
        franshise: settings.franchise?.franshise || '',
        name: settings.subscribe?.name || '',
        phone: settings.subscribe?.phone || '',
      })
    }
  }, [settings, reset])

  // Mutation for updating all settings
  const updateMutation = useMutation({
    mutationFn: async (formData) => {
      // Update all settings in parallel
      await Promise.all([
        updateNationalCurrency({ currency: formData.currency }),
        updateFranchiseCity({ sity: formData.sity }),
        updateFranchise({ franshise: formData.franshise }),
        updateSubscribe({ name: formData.name, phone: formData.phone }),
      ])
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['global-settings'] })
      toastService.success({
        title: t('common.success'),
        description: t('settings.globalSettingsUpdated'),
      })
    },
    onError: (error) => {
      toastService.error({
        title: t('common.error'),
        description: error.message || t('settings.globalSettingsUpdateError'),
      })
    },
  })

  const onSubmit = (data) => {
    updateMutation.mutate(data)
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" />
      </Box>
    )
  }

  return (
    <Box p={8}>
      <Heading size="2xl" mb={6}>
        {t('navigation.settingsGlobal')}
      </Heading>

      <Box maxW="1000px">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={6}>
            {/* National Currency */}
            <Fieldset>
              <Fieldset.Legend fontSize="lg" fontWeight="600">
                {t('settings.nationalCurrency')}
              </Fieldset.Legend>
              <Fieldset.Content>
                <Controller
                  name="currency"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      label={t('settings.currencySymbol')}
                      placeholder={t('settings.currencyPlaceholder')}
                      error={error?.message}
                      {...field}
                    />
                  )}
                />
              </Fieldset.Content>
            </Fieldset>

            {/* Franchise City */}
            <Fieldset>
              <Fieldset.Legend fontSize="lg" fontWeight="600">
                {t('settings.franchiseCity')}
              </Fieldset.Legend>
              <Fieldset.Content>
                <Controller
                  name="sity"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      label={t('settings.city')}
                      placeholder={t('settings.cityPlaceholder')}
                      error={error?.message}
                      {...field}
                    />
                  )}
                />
              </Fieldset.Content>
            </Fieldset>

            {/* Franchise */}
            <Fieldset>
              <Fieldset.Legend fontSize="lg" fontWeight="600">
                {t('settings.franchise')}
              </Fieldset.Legend>
              <Fieldset.Content>
                <Controller
                  name="franshise"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      label={t('settings.franchiseName')}
                      placeholder={t('settings.franchisePlaceholder')}
                      error={error?.message}
                      {...field}
                    />
                  )}
                />
              </Fieldset.Content>
            </Fieldset>

            {/* Subscribe (Signature) */}
            <Fieldset>
              <Fieldset.Legend fontSize="lg" fontWeight="600">
                {t('settings.subscribe')}
              </Fieldset.Legend>
              <Fieldset.Content>
                <SimpleGrid columns={2} gap={4}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: t('validation.required') }}
                    render={({ field, fieldState: { error } }) => (
                      <Input
                        label={t('settings.subscribeName')}
                        placeholder={t('settings.subscribeNamePlaceholder')}
                        error={error?.message}
                        {...field}
                      />
                    )}
                  />
                  <Controller
                    name="phone"
                    control={control}
                    rules={{ required: t('validation.required') }}
                    render={({ field, fieldState: { error } }) => (
                      <Input
                        label={t('settings.subscribePhone')}
                        placeholder={t('settings.subscribePhonePlaceholder')}
                        error={error?.message}
                        {...field}
                      />
                    )}
                  />
                </SimpleGrid>
              </Fieldset.Content>
            </Fieldset>

            {/* Actions */}
            <HStack justify="flex-end" spacing={3} pt={4}>
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={() => reset()}
                disabled={updateMutation.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                size="md"
                isLoading={updateMutation.isPending}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </Stack>
        </form>
      </Box>
    </Box>
  )
}

export default GlobalSettings
